'use client'

import { createContext, useContext, useEffect, useState } from 'react'

import { type GaSessionInfo, getGaSessionInfo } from 'clients/gtag'

const EMPTY_GA_SESSION_INFO = {
  ga_session_id: '',
  ga_session_number: '',
} as const satisfies GaSessionInfo

const GaSessionInfoContext = createContext<GaSessionInfo>(EMPTY_GA_SESSION_INFO)

/**
 * @todo This provider currently provides empty strings for ga_session_id and
 * ga_session_number when the real values are not available. A more robust
 * implementation would set these to a value of a different type (like null or
 * undefined) when unavailable, to enable TypeScript to prompt consumers to
 * correctly handle these cases. If it is importnat that they always be defined,
 * we could then restructure to ensure this (e.x. by making the rendering of
 * content which requires these values dependent on their availability).
 */
export const GaSessionInfoProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [gaSessionInfo, setGaSessionInfo] = useState<GaSessionInfo>(
    EMPTY_GA_SESSION_INFO,
  )

  useEffect(() => {
    const getAndSetGaSessionInfo = async () => {
      const { ga_session_id, ga_session_number } = await getGaSessionInfo()

      setGaSessionInfo({
        ga_session_id: ga_session_id ?? '',
        ga_session_number: ga_session_number ?? '',
      })
    }

    getAndSetGaSessionInfo()
  }, [])

  return (
    <GaSessionInfoContext.Provider value={gaSessionInfo}>
      {children}
    </GaSessionInfoContext.Provider>
  )
}

export const useGaSessionInfo = (): GaSessionInfo =>
  useContext(GaSessionInfoContext)
